'use client';
import React, { useState } from 'react';
import { BarChart2, TrendingUp, Users, Activity, Filter } from 'lucide-react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';


const AdvancedRevenueChart = dynamic(() => import('./AdvancedRevenueChart'), { ssr: false });
const CohortRetentionChart = dynamic(() => import('./CohortRetentionChart'), { ssr: false });
const ChannelBreakdownChart = dynamic(() => import('./ChannelBreakdownChart'), { ssr: false });
const ConversionTrendChart = dynamic(() => import('./ConversionTrendChart'), { ssr: false });

const metrics = [
  {
    id: 'am-mrr',
    label: 'Monthly Recurring Revenue',
    value: '$1,428,400',
    change: +12.4,
    sub: 'ARR: $17.14M',
    icon: TrendingUp,
    color: 'primary',
  },
  {
    id: 'am-ndr',
    label: 'Net Dollar Retention',
    value: '118.4%',
    change: +3.2,
    sub: 'Expansion + upsell',
    icon: Activity,
    color: 'positive',
  },
  {
    id: 'am-cac',
    label: 'Customer Acq. Cost',
    value: '$1,240',
    change: -8.1,
    sub: 'LTV:CAC = 4.8x',
    icon: Users,
    color: 'warning',
  },
  {
    id: 'am-nps',
    label: 'Net Promoter Score',
    value: '62',
    change: +4,
    sub: 'Up from 58 last quarter',
    icon: BarChart2,
    color: 'info',
  },
];

const colorMap: Record<string, string> = {
  primary: 'bg-primary/10 text-primary',
  positive: 'bg-positive/10 text-positive',
  warning: 'bg-warning/10 text-warning',
  info: 'bg-info/10 text-info',
};

export default function AnalyticsDashboardContent() {
  const [dateRange, setDateRange] = useState('30D');

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Analytics Deep Dive</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Advanced metrics and trend analysis — May 2026
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-muted-foreground" />
          <div className="flex items-center bg-muted rounded-lg p-1">
            {['7D', '30D', '90D', 'YTD'].map((p) => (
              <button
                key={`dr-${p}`}
                onClick={() => setDateRange(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 btn-press ${
                  dateRange === p
                    ? 'bg-card text-foreground shadow-card'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {metrics.map((m) => {
          const Icon = m.icon;
          const isPositive = m.change >= 0;
          return (
            <div key={m.id} className="card-surface hover-lift p-5">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[m.color]}`}>
                  <Icon size={18} />
                </div>
                <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
                  isPositive ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'
                }`}>
                  {isPositive ? '+' : ''}{m.change}%
                </span>
              </div>
              <p className="label-caps text-muted-foreground mb-1">{m.label}</p>
              <p className="metric-value text-2xl text-foreground">{m.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{m.sub}</p>
            </div>
          );
        })}
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-2 gap-6">
        <div className="card-surface p-5">
          <h2 className="text-base font-semibold text-foreground mb-1">MRR Growth Breakdown</h2>
          <p className="text-sm text-muted-foreground mb-4">New, expansion, contraction, churn</p>
          <AdvancedRevenueChart />
        </div>
        <div className="card-surface p-5">
          <h2 className="text-base font-semibold text-foreground mb-1">Conversion Rate Trend</h2>
          <p className="text-sm text-muted-foreground mb-4">Lead-to-trial and trial-to-paid rates</p>
          <ConversionTrendChart />
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-2 gap-6">
        <div className="card-surface p-5">
          <h2 className="text-base font-semibold text-foreground mb-1">Revenue by Channel</h2>
          <p className="text-sm text-muted-foreground mb-4">Direct, partner, self-serve, marketplace</p>
          <ChannelBreakdownChart />
        </div>
        <div className="card-surface p-5">
          <h2 className="text-base font-semibold text-foreground mb-1">Cohort Retention</h2>
          <p className="text-sm text-muted-foreground mb-4">Monthly cohort retention at 30/60/90 days</p>
          <CohortRetentionChart />
        </div>
      </div>
    </div>
  );
}